import Box from '@mui/material/Box'
import Typography from '@mui/material/Typography'
import Card from '@mui/material/Card'
import CardContent from '@mui/material/CardContent'
import Chip from '@mui/material/Chip'
import Accordion from '@mui/material/Accordion'
import AccordionSummary from '@mui/material/AccordionSummary'
import AccordionDetails from '@mui/material/AccordionDetails'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import EmailIcon from '@mui/icons-material/Email'
import PhoneIcon from '@mui/icons-material/Phone'
import ChatIcon from '@mui/icons-material/Chat'
import PageHeader from '../../common/PageHeader'
import Section from '../../common/Section'
import Alert from '../../common/Alert'
import { InlineCode } from '../../common/CodeBlock'
import { useLocale } from '../../../i18n/useLocale'

export default function Support() {
  const { support, phone, locale, payment, brand } = useLocale()

  const faqs = [
  {
    question: 'What parcel sizes are supported?',
    answer: `${brand.name} supports three parcel sizes: Small (S) - up to 365x610x100 mm, 25kg; Medium (M) - up to 365x610x200, 25kg; Large (L) - up to 365x610x380, 25kg. Choose the appropriate size during parcel creation.`,
  },
  {
    question: 'How do I get my API credentials?',
    answer: `Contact the ${brand.name} partner team at ${support.salesEmail}. After onboarding, you will receive your API Key for both sandbox and production environments.`,
  },
  {
    question: 'What phone number format should I use?',
    answer: `Use ${locale.country} phone numbers in any of these formats: ${phone.validFormats}. The API will normalize the format automatically.`,
  },
  {
    question: 'How long does a parcel stay in the APM?',
    answer: 'Parcels are held for 3 days after delivery to the APM. After this period, uncollected parcels are returned to the sender. Customers receive SMS notifications when the parcel arrives and before the collection deadline.',
  },
  {
    question: 'Can I track parcels using my own reference number?',
    answer: `Yes! When creating a parcel, include your order ID in the referenceCode or uniqueParcelNumber field. You can then query status using either your reference or the ${brand.name} parcel number.`,
  },
  ...(payment.mobileMoney ? [{
    question: `What happens if ${payment.mobileMoney.name} payment fails?`,
    answer: 'If payment fails due to timeout or user cancellation, you can retry with the same parameters. The system checks for duplicate payments within 24 hours and will update the existing payment record if found.',
  }] : []),
  {
    question: 'Is the APM Finder widget free to use?',
    answer: 'Yes, the APM Finder widget is provided free of charge to all integration partners. Simply embed the iframe in your checkout page.',
  },
  {
    question: 'How do I handle webhook callbacks?',
    answer: `Configure your callback URL and Basic-auth credentials in the Customer Portal under Settings. ${brand.name} will send a POST request to your URL on every parcel status change — see the Parcel Tracking guide for the payload contract.`,
  },
  {
    question: 'What are the API rate limits?',
    answer: 'Sandbox has no rate limits. Production allows 100 requests per minute per API key for most endpoints. Batch endpoints have higher limits. Contact support if you need increased limits.',
  },
  {
    question: 'Can I white-label the APM Finder widget?',
    answer: 'White-label options are available for enterprise partners. Contact your account manager to discuss custom branding, colors, and domain configuration.',
  },
]

  return (
    <Box>
      <PageHeader
        title="Help & Support"
        description={`Get help with your ${brand.name} integration. Browse FAQs or contact our support team.`}
        breadcrumbs={[{ label: 'Support' }]}
      />

      <Section title="Contact Us" divider={false}>
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 4 }}>
          <Card variant="outlined" sx={{ flex: '1 1 280px' }}>
            <CardContent sx={{ textAlign: 'center', py: 3 }}>
              <EmailIcon sx={{ fontSize: 40, color: 'primary.main', mb: 1 }} />
              <Typography variant="h6" sx={{ mb: 1 }}>Email Support</Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                For technical questions and integration help
              </Typography>
              <Chip
                label={support.email}
                color="primary"
                variant="outlined"
                component="a"
                href={`mailto:${support.email}`}
                clickable
              />
            </CardContent>
          </Card>

          <Card variant="outlined" sx={{ flex: '1 1 280px' }}>
            <CardContent sx={{ textAlign: 'center', py: 3 }}>
              <PhoneIcon sx={{ fontSize: 40, color: 'primary.main', mb: 1 }} />
              <Typography variant="h6" sx={{ mb: 1 }}>Phone Support</Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                {support.hours}
              </Typography>
              <Chip
                label={support.phone}
                color="primary"
                variant="outlined"
                component="a"
                href={`tel:${support.phone.replace(/\s/g, '')}`}
                clickable
              />
            </CardContent>
          </Card>

          <Card variant="outlined" sx={{ flex: '1 1 280px' }}>
            <CardContent sx={{ textAlign: 'center', py: 3 }}>
              <ChatIcon sx={{ fontSize: 40, color: 'primary.main', mb: 1 }} />
              <Typography variant="h6" sx={{ mb: 1 }}>Account Manager</Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                For business discussions and custom solutions
              </Typography>
              <Chip
                label="Contact your AM"
                color="primary"
                variant="outlined"
              />
            </CardContent>
          </Card>
        </Box>

        <Alert type="info" title="Response Times">
          Email support typically responds within 4 business hours. For urgent production issues,
          please call our support line directly.
        </Alert>
      </Section>

      <Section title="Frequently Asked Questions">
        <Box sx={{ mb: 4 }}>
          {faqs.map((faq, index) => (
            <Accordion
              key={index}
              sx={{
                '&:before': { display: 'none' },
                boxShadow: 'none',
                border: '1px solid',
                borderColor: 'grey.200',
                mb: 1,
                borderRadius: '8px !important',
                '&:first-of-type': { borderRadius: '8px !important' },
                '&:last-of-type': { borderRadius: '8px !important' },
              }}
            >
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography variant="body1" sx={{ fontWeight: 500 }}>
                  {faq.question}
                </Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Typography variant="body2" color="text.secondary">
                  {faq.answer}
                </Typography>
              </AccordionDetails>
            </Accordion>
          ))}
        </Box>
      </Section>

      <Section title="Common Error Codes">
        <Typography variant="body1" sx={{ mb: 2 }}>
          Quick reference for common API error responses:
        </Typography>

        <Box component="ul" sx={{ pl: 3, '& li': { mb: 1.5 } }}>
          <li>
            <InlineCode>400 Bad Request</InlineCode> - Invalid request body or parameters. Check validation errors in response.
          </li>
          <li>
            <InlineCode>401 Unauthorized</InlineCode> - Invalid or missing API key. Verify your credentials.
          </li>
          <li>
            <InlineCode>403 Forbidden</InlineCode> - Valid credentials but insufficient permissions.
          </li>
          <li>
            <InlineCode>404 Not Found</InlineCode> - Resource not found (e.g., invalid parcel number or APM ID).
          </li>
          <li>
            <InlineCode>422 Unprocessable Entity</InlineCode> - Request understood but cannot be processed (e.g., duplicate parcel).
          </li>
          <li>
            <InlineCode>429 Too Many Requests</InlineCode> - Rate limit exceeded. Wait and retry.
          </li>
          <li>
            <InlineCode>500 Internal Server Error</InlineCode> - Server error. Contact support if persistent.
          </li>
        </Box>
      </Section>

      <Section title="Useful Links">
        <Box component="ul" sx={{ pl: 3, '& li': { mb: 1 } }}>
          <li>
            <a href="/getting-started/overview">Getting Started Guide</a> - Introduction to the {brand.name} platform
          </li>
          <li>
            <a href="/getting-started/authentication">Authentication Guide</a> - API key setup
          </li>
          <li>
            <a href="/api-reference/parcel-api">API Reference</a> - Complete API documentation
          </li>
          <li>
            <a href="/testing/sandbox">Sandbox Environment</a> - Test credentials and setup
          </li>
          <li>
            <a href="/widgets/apm-finder">APM Finder Widget</a> - Embed the location picker
          </li>
        </Box>
      </Section>
    </Box>
  )
}
